import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide, useTheme } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
  SlideAnimationProvider,
} from "contexts/SlideAnimation";
import { SubscriptionContext } from "contexts/Subscription";
import lodash from "lodash";
import { useContext, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  OrganizationAutopilotProfile,
  OrganizationAutopilotProfileStatus,
} from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import Alert from "components/Alert";
import Loading from "components/Loading";
import { PAGE_VISITED } from "constants/trackingProps";
import ProOnboardingAboutForm from "features/Auth/Onboarding/ProOnboarding/AboutForm";
import ProOnboardingTemplatesForm from "features/Auth/Onboarding/ProOnboarding/TemplatesForm";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { getSource, identifyUser, trackEvent } from "utils/tracking";
import { useAlert } from "utils/useAlert";

import AutopilotOnboardingCompleteScreen from "./CompleteScreen";
import AutopilotOnboardingImportContacts from "./ImportContacts";
import AutopilotOnboardingWelcomeForm from "./Welcome";
import {
  AutopilotOnboardingStep,
  DefaultAutopilotOnboardingSteps,
  StepToRoute,
} from "./schema";
import styles from "./styles";

const AutopilotOnboarding = () => {
  const location = useLocation();
  const {
    currentOrg,
    setCurrentUser,
    setCurrentOrg,
    setUserNotifications,
    setUserIntegrations,
    setEmailSettings,
    organizationAutopilotProfile,
    setOrganizationAutopilotProfile,
    setOrganizationProProfile,
    setProfile,
  } = useContext(OrganizationUserContext);
  const theme = useTheme();
  const slideContext = useContext(SlideAnimationContext);
  const { setSubscription } = useContext(SubscriptionContext);
  const { fetchOutreachTemplates } = useContext(OutreachTemplatesContext);
  const { slideProps } = useContext(SlideAnimationContext);
  const [message, severity, setAlert, closeAlert] = useAlert();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [fetchUserLoading, setFetchUserLoading] = useState(true);
  const [nextLoading, setNextLoading] = useState(false);

  const [onboardingForm, setOnboardingForm] = useState<Map>({});

  const fetchUserInfo = async () => {
    try {
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      setFetchUserLoading(false);
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      setSubscription(res.subscription);
      setUserIntegrations(res.userIntegrations);
      setUserNotifications(res.userNotifications);
      setEmailSettings(res.emailSetting);
      setOnboardingForm((prev) => ({
        ...prev,
        ...res.organizationProfile,
        name: res.organizationUser.name,
      }));

      setOrganizationAutopilotProfile(res.organizationAutopilotProfile);
      setOrganizationProProfile(res.organizationProProfile);
      setProfile(res.organizationProfile);
      redirectToRightPage(res.organizationAutopilotProfile);
      slideContext.slideInUp();
    } catch (error) {}
  };

  const redirectToRightPage = (
    autopilotProfile: OrganizationAutopilotProfile,
  ) => {
    if (
      autopilotProfile?.status ===
        OrganizationAutopilotProfileStatus.completed ||
      autopilotProfile?.status === OrganizationAutopilotProfileStatus.later
    ) {
      navigate(`/${routes.home}`);
      return;
    }

    if (
      autopilotProfile?.onboardingState?.steps &&
      !lodash.isEqual(
        autopilotProfile.onboardingState.steps,
        DefaultAutopilotOnboardingSteps,
      )
    ) {
      trackAutopilotStep(AutopilotOnboardingStep.welcome);
      navigate(`/${routes.onboardingAutopilotWelcome}${location.search}`);
      return;
    }

    const currentStep = Math.min(
      autopilotProfile?.onboardingState?.currentStepIndex || 0,
      DefaultAutopilotOnboardingSteps?.length - 1,
    );
    const stepName = autopilotProfile?.onboardingState?.steps[
      currentStep
    ] as AutopilotOnboardingStep;

    if (stepName && stepName in StepToRoute) {
      const route = StepToRoute[stepName];
      trackAutopilotStep(stepName);
      navigate(`/${route}${location.search}`);
      return;
    }

    navigate(`/${routes.onboardingAutopilotWelcome}${location.search}`);
  };

  const finishSetUpLater = async () => {
    trackEvent("Autopilot Onboarding - Finish Set Up Later", {
      route: location.pathname,
    });

    navigate(`/${routes.home}`);
    updateAutopilotStatus(OrganizationAutopilotProfileStatus.later);
  };

  const updateAutopilotStatus = async (
    status: OrganizationAutopilotProfileStatus,
  ) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/autopilot-status`,
        "PATCH",
        {},
        {
          status,
        },
      );
      setOrganizationAutopilotProfile(res.organizationAutopilotProfile);
    } catch (error) {
      setAlert(
        error?.message ||
          "Something went wrong. Please refresh and retry again",
        "error",
      );
    }
  };

  const handleNext = async (step: AutopilotOnboardingStep) => {
    const indexOfStep = DefaultAutopilotOnboardingSteps.findIndex(
      (s) => s === step,
    );
    if (indexOfStep === -1) {
      return;
    }
    if (
      indexOfStep === DefaultAutopilotOnboardingSteps.length - 1 ||
      step === AutopilotOnboardingStep.complete
    ) {
      if (
        organizationAutopilotProfile?.status !==
        OrganizationAutopilotProfileStatus.completed
      ) {
        await updateAutopilotStatus(
          OrganizationAutopilotProfileStatus.completed,
        );
      }
      navigate(`/${routes.autopilot}`);
      return;
    }
    const nextStepName = DefaultAutopilotOnboardingSteps[indexOfStep + 1];
    const nextPath = StepToRoute[nextStepName];
    navigate(`/${nextPath}${location.search}`);

    trackAutopilotStep(nextStepName);
  };

  const handleBack = (step: AutopilotOnboardingStep) => {
    const indexOfStep = DefaultAutopilotOnboardingSteps.findIndex(
      (s) => s === step,
    );
    if (indexOfStep === -1 || indexOfStep === 0) {
      return;
    }
    if (indexOfStep === DefaultAutopilotOnboardingSteps.length - 1) {
      navigate(`/${routes.home}`);
    }

    const backStepName = DefaultAutopilotOnboardingSteps[indexOfStep - 1];
    const backPath = StepToRoute[backStepName];
    navigate(`/${backPath}${location.search}`);

    trackAutopilotStep(backStepName);
  };

  const trackAutopilotStep = async (step: AutopilotOnboardingStep) => {
    setNextLoading(true);
    try {
      const { organizationAutopilotProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/track-autopilot-step`,
        "POST",
        {},
        {
          page: location.pathname,
          source: getSource(),
          currentStepName: step,
          steps: DefaultAutopilotOnboardingSteps,
        },
      );
      if (organizationAutopilotProfile) {
        setOrganizationAutopilotProfile(organizationAutopilotProfile);
      }
      setNextLoading(false);
    } catch (error) {
      setNextLoading(false);
    }
  };

  useEffect(() => {
    fetchUserInfo();
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchOutreachTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return fetchUserLoading ? (
    <Loading />
  ) : (
    <>
      <Grid container sx={styles.root}>
        <Slide
          in={slideProps.in}
          direction={slideProps.direction}
          easing={{
            enter: theme.transitions.easing.easeInOut,
            exit: theme.transitions.easing.easeIn,
          }}
          timeout={{
            enter: ENTER_DELAY,
            exit: EXIT_DELAY,
          }}
        >
          <Grid
            sx={[
              styles.formContainer,
              {
                maxWidth: {
                  md: isPage(routes.onboardingAutopilotTemplates)
                    ? "2400px"
                    : "1200px",
                },
              },
            ]}
            item
            xs={12}
          >
            <Routes>
              <Route
                path="*"
                element={
                  <Navigate
                    to={`/${routes.onboardingAutopilotWelcome}`}
                    replace
                  />
                }
              />

              <Route
                path="welcome/*"
                element={
                  <AutopilotOnboardingWelcomeForm
                    handleNext={() =>
                      handleNext(AutopilotOnboardingStep.welcome)
                    }
                    finishSetUpLater={finishSetUpLater}
                  />
                }
              />

              <Route
                path="about/*"
                element={
                  <ProOnboardingAboutForm
                    onboardingForm={onboardingForm}
                    setOnboardingForm={setOnboardingForm}
                    handleBack={() => handleBack(AutopilotOnboardingStep.about)}
                    handleNext={() => handleNext(AutopilotOnboardingStep.about)}
                    onFinishSetUpLater={finishSetUpLater}
                  />
                }
              />

              <Route
                path="templates/*"
                element={
                  <ProOnboardingTemplatesForm
                    handleNext={() =>
                      handleNext(AutopilotOnboardingStep.templates)
                    }
                    handleBack={() =>
                      handleBack(AutopilotOnboardingStep.templates)
                    }
                    onFinishSetUpLater={finishSetUpLater}
                    buttonNextLabel="Continue To Import Contacts"
                  />
                }
              />

              <Route
                path="import-contacts/*"
                element={
                  <AutopilotOnboardingImportContacts
                    handleNext={() =>
                      handleNext(AutopilotOnboardingStep.importContacts)
                    }
                    handleBack={() =>
                      handleBack(AutopilotOnboardingStep.importContacts)
                    }
                    onFinishSetUpLater={finishSetUpLater}
                  />
                }
              />

              <Route
                path="complete/*"
                element={
                  <AutopilotOnboardingCompleteScreen
                    handleNext={() =>
                      handleNext(AutopilotOnboardingStep.complete)
                    }
                    handleBack={() =>
                      handleBack(AutopilotOnboardingStep.complete)
                    }
                    nextLoading={nextLoading}
                  />
                }
              />
            </Routes>
          </Grid>
        </Slide>
      </Grid>

      <Alert message={message} severity={severity} closeAlert={closeAlert} />
    </>
  );
};

export default function AutopilotOnboardingWrapper() {
  return (
    <SlideAnimationProvider>
      <AutopilotOnboarding />
    </SlideAnimationProvider>
  );
}
